import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { core } from "../../lib/api";
import { streamSse } from "../../lib/sse";
import { useToast } from "../../components/ui/toast";
import type { Agent, AgentAnalytics, Credits, DashboardKpis, Folder, Workflow } from "./types";

/**
 * Safely coerce an API response to an array.
 *
 * Some endpoints return a bare `T[]`, others wrap it in an object like
 * `{ agents: T[] }`.  This helper handles both shapes **without guessing**
 * — the caller must specify the expected wrapper key.
 *
 *   asArray(data, "agents")   // { agents: [...] } → [...]
 *   asArray(data)             // [...] → [...]  (no unwrap)
 *
 * Returns `[]` only for null / undefined.  Any other unexpected shape
 * triggers a dev-mode warning so it surfaces immediately.
 */
export function asArray<T>(data: unknown, wrapperKey?: string): T[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as T[];
  if (wrapperKey && typeof data === "object") {
    const inner = (data as Record<string, unknown>)[wrapperKey];
    if (Array.isArray(inner)) return inner as T[];
  }
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `[asArray] Unexpected API shape (wrapperKey=${wrapperKey ?? "none"})`,
      data,
    );
  }
  return [];
}

/**
 * @deprecated Use `asArray(data, key)` with an explicit key instead.
 * Kept temporarily for call sites outside features/home that still import it.
 */
export const extractList = (data: any) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const key = Object.keys(data).find((k) => Array.isArray(data[k]));
    if (key) return data[key];
  }
  return [];
};

/* FIRST PAINT BUDGET: 6 parallel GETs + 1 SSE. staleTimes keep it silent after. */

// Backend returns a flat array for ?view=summary but wraps in {"agents": []}
// when the account has zero agents. Explicit key handles both shapes.
export const useAgents = () =>
  useQuery({
    queryKey: ["agents"],
    queryFn: () => core<any>("/api/v2/agents?view=summary").then((d) => asArray<Agent>(d, "agents")),
    staleTime: 60_000,
  });
export const useWorkflows = () =>
  useQuery({ queryKey: ["workflows"], queryFn: () => core<any>("/api/v2/multi-agent-prompts").then((d) => asArray<Workflow>(d, "prompts")), staleTime: 60_000 });
export const useFolders = () =>
  useQuery({
    queryKey: ["folders"],
    queryFn: () => core<any>("/api/v2/agent-folders").then((d) => asArray<Folder>(d, "folders")),
    staleTime: 60_000,
  });
export const useDashKpis = (params?: { start_date?: string; end_date?: string }) => {
  const qs = params?.start_date ? `?start_date=${params.start_date}&end_date=${params.end_date}` : "";
  return useQuery({
    queryKey: ["analytics", "dashboard", params?.start_date, params?.end_date],
    queryFn: () => core<DashboardKpis>(`/api/v2/analytics/dashboard${qs}`),
    staleTime: 60_000,
  });
};
/** Lifetime call total. The analytics endpoints default to a 7-day window, so
 *  they can't answer "total platform volume" -- this sums the stored counters. */
export const useTotalCalls = () =>
  useQuery({
    queryKey: ["agents", "total-calls"],
    queryFn: () => core<{ total_calls: number }>("/api/v2/agents/total-calls"),
    staleTime: 60_000,
  });
export const useAgentsAnalytics = (params?: { start_date?: string; end_date?: string }) => {
  const qs = params?.start_date ? `?start_date=${params.start_date}&end_date=${params.end_date}` : "";
  return useQuery({
    queryKey: ["analytics", "agents", params?.start_date, params?.end_date],
    queryFn: () => core<any>(`/api/v2/analytics/agents${qs}`).then((d) => asArray<AgentAnalytics>(d, "agents")),
    staleTime: 60_000,
  });
};
export const useCredits = () =>
  useQuery({ queryKey: ["credits"], queryFn: () => core<Credits>("/api/v2/credits/me"), staleTime: 300_000 });
export const useCampaignNames = () =>
  useQuery({ queryKey: ["campaigns", "names"], queryFn: () => core<any>("/api/v1/campaigns/names").then((d) => asArray<{ name: string }>(d, "items")), staleTime: 300_000 });
export const useVoices = () =>
  useQuery({ queryKey: ["voice", "options"], queryFn: () => core<any>("/api/v1/voice/options").then((d) => asArray<{ local_voice_name: string }>(d, "voices")), staleTime: 300_000 });

/** Persistent SSE — low-balance toasts only. No polling anywhere on this page. */
export function useCreditAlerts() {
  const toast = useToast();
  useEffect(() => {
    const s = streamSse("/api/v2/credit-alerts/stream", undefined, (type, d) => {
      if (type === "low_balance") toast.warning("Wallet running low", String(d.balance ?? ""));
    });
    return s.close;
  }, [toast]);
}

/* Mutations — optimistic where safe, invalidate-scoped always. */
export function useMoveAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, folderId }: { agentId: string; folderId: string | null }) =>
      core(`/api/v2/agent-folders/move-agent/${agentId}`, { method: "POST", body: JSON.stringify({ folder_id: folderId }) }),
    onMutate: async ({ agentId, folderId }) => {
      await qc.cancelQueries({ queryKey: ["agents"] });
      const prev = qc.getQueryData<Agent[]>(["agents"]);
      qc.setQueryData<Agent[]>(["agents"], (old) => old?.map((a) => (a.agent_id === agentId ? { ...a, folder_id: folderId } : a)));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(["agents"], ctx?.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });
}

/** Shared by the folder sidebar and the "Move to Folder" modal so counts never drift apart. */
export function useFolderCounts() {
  const { data: agents = [] } = useAgents();
  const { data: workflows = [] } = useWorkflows();
  return (id: string | null) => (
    agents.filter((agent) => (id ? agent.folder_id === id : true)).length
    + workflows.filter((workflow) => (id ? workflow.folder_id === id : true)).length
  );
}

export function useFolderMutations() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["folders"] });
  return {
    create: useMutation({ mutationFn: (name: string) => core("/api/v2/agent-folders", { method: "POST", body: JSON.stringify({ folder_name: name }) }), onSettled: inv }),
    rename: useMutation({ mutationFn: ({ id, name }: { id: string; name: string }) => core(`/api/v2/agent-folders/${id}`, { method: "PATCH", body: JSON.stringify({ folder_name: name }) }), onSettled: inv }),
    remove: useMutation({ mutationFn: (id: string) => core(`/api/v2/agent-folders/${id}`, { method: "DELETE" }), onSettled: () => { inv(); qc.invalidateQueries({ queryKey: ["agents"] }); } }),
  };
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const pending = await core<{ has_pending: boolean }>(`/api/v2/agents/${id}/has-pending-schedule`);
      if (pending.has_pending) throw new Error("Agent has a pending schedule. Cancel it first.");
      return core(`/api/v2/agents/${id}`, { method: "DELETE" });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });
}

export function useRenameAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      core(`/api/v1/agents/${id}`, { method: "PATCH", body: JSON.stringify({ agent_name: name }) }),
    onMutate: async ({ id, name }) => {
      await qc.cancelQueries({ queryKey: ["agents"] });
      const prev = qc.getQueryData<Agent[]>(["agents"]);
      qc.setQueryData<Agent[]>(["agents"], (old) => old?.map((a) => (a.agent_id === id ? { ...a, agent_name: name, name: name } : a)));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(["agents"], ctx?.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });
}
